import { ReactElement } from 'react';
import prettyBytes from 'pretty-bytes';
import { SubscriptionUsage } from '../types';
import classNames from '../utilities/class-names';
import formatDate from '../utilities/format-date';
import config from '../config';
import subscriptionRatingColours from '../utilities/subscription-rating-colours';
import { Rating } from './Rating';
import './UsageBar.scss';

type UsageBarProps = {
  usage?: SubscriptionUsage | null;
  className?: string;

  // Show the stored bytes row as well as requests
  showStorage?: boolean;
};

function formatNumber(value: number): string {
  return value.toLocaleString('en-GB');
}

export function UsageBar({
  usage,
  className,
  showStorage = true,
}: UsageBarProps): ReactElement | null {
  if (!usage) {
    return null;
  }

  const unlimitedRequests = usage.requestAllowance === null;
  const requestAllowance = usage.requestAllowance ?? 0;
  const requestsUsed = usage.requestCount;

  return (
    <div className={classNames('usage-bar', className)}>
      <div className="usage-bar-row">
        <div className="usage-bar-label">
          <span>Requests this month</span>
          <span className="usage-bar-figure">
            {unlimitedRequests ? (
              <>{formatNumber(requestsUsed)} used</>
            ) : (
              <>
                {formatNumber(requestsUsed)} / {formatNumber(requestAllowance)}
              </>
            )}
          </span>
        </div>
        {!unlimitedRequests && (
          <Rating
            rating={requestsUsed}
            maxRating={requestAllowance}
            colours={subscriptionRatingColours}
            relativeThresholds
          />
        )}
      </div>

      {showStorage && usage.storageAllowance !== null && (
        <div className="usage-bar-row">
          <div className="usage-bar-label">
            <span>Stored data</span>
            <span className="usage-bar-figure">
              {prettyBytes(usage.storageBytes)} /{' '}
              {prettyBytes(usage.storageAllowance)}
            </span>
          </div>
          <Rating
            rating={usage.storageBytes}
            maxRating={usage.storageAllowance}
            colours={subscriptionRatingColours}
            relativeThresholds
          />
        </div>
      )}

      <div className="usage-bar-footnotes">
        {usage.credits > 0 && (
          <span className="usage-bar-credits">
            {formatNumber(usage.credits)} prepaid requests held
          </span>
        )}
        <span className="usage-bar-reset">
          Resets {formatDate(usage.periodEnd, config.pageShortDateFormat)}
        </span>
      </div>
    </div>
  );
}
